class ChakrikkaMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.triggers = [];
    this.panels = [];
    this.backdrop = null;
    this.activeIndex = null;
    this.closeTimeout = null;
    this.openTimeout = null;
    this.CLOSE_DELAY = 150;
    this.OPEN_DELAY = 80;
  }

  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-mega-panel]'));
    this.backdrop = this.querySelector('[data-mega-backdrop]');

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', this.handleTriggerEnter.bind(this));
      trigger.addEventListener('mouseleave', this.handleTriggerLeave.bind(this));
      trigger.addEventListener('click', this.handleTriggerClick.bind(this));
      trigger.addEventListener('keydown', this.handleTriggerKeydown.bind(this));
    });

    this.panels.forEach((panel) => {
      panel.addEventListener('mouseenter', this.handlePanelEnter.bind(this));
      panel.addEventListener('mouseleave', this.handlePanelLeave.bind(this));
      panel.addEventListener('keydown', this.handlePanelKeydown.bind(this));
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  indexFor(trigger) {
    return trigger.getAttribute('data-mega-trigger');
  }

  panelFor(index) {
    return this.panels.find((p) => p.getAttribute('data-mega-panel') === String(index));
  }

  triggerFor(index) {
    return this.triggers.find((t) => t.getAttribute('data-mega-trigger') === String(index));
  }

  open(index) {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);

    if (this.activeIndex === index) return;

    if (this.activeIndex !== null) {
      var oldPanel = this.panelFor(this.activeIndex);
      var oldTrigger = this.triggerFor(this.activeIndex);
      if (oldPanel) oldPanel.classList.remove('is-active');
      if (oldTrigger) oldTrigger.setAttribute('aria-expanded', 'false');
    }

    this.activeIndex = index;
    var panel = this.panelFor(index);
    var trigger = this.triggerFor(index);

    if (panel) {
      panel.classList.add('is-active');
      panel.setAttribute('aria-hidden', 'false');
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (this.backdrop) this.backdrop.classList.add('is-visible');

    this.dispatchEvent(new CustomEvent('megamenu:open', { bubbles: true }));
  }

  close() {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);

    if (this.activeIndex === null) return;

    var panel = this.panelFor(this.activeIndex);
    var trigger = this.triggerFor(this.activeIndex);
    if (panel) {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (this.backdrop) this.backdrop.classList.remove('is-visible');

    this.activeIndex = null;
    this.dispatchEvent(new CustomEvent('megamenu:close', { bubbles: true }));
  }

  scheduleClose() {
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => this.close(), this.CLOSE_DELAY);
  }

  cancelClose() {
    clearTimeout(this.closeTimeout);
  }

  handleTriggerEnter(e) {
    var index = this.indexFor(e.currentTarget);
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);

    if (this.activeIndex !== null) {
      this.open(index);
    } else {
      this.openTimeout = setTimeout(() => this.open(index), this.OPEN_DELAY);
    }
  }

  handleTriggerLeave() {
    this.scheduleClose();
  }

  handleTriggerClick(e) {
    var index = this.indexFor(e.currentTarget);
    if (this.activeIndex === index) {
      this.close();
    } else {
      this.open(index);
    }
  }

  handleTriggerKeydown(e) {
    if (e.key === 'Enter' && e.currentTarget.tagName === 'A') {
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleTriggerClick(e);

      if (this.activeIndex !== null) {
        var panel = this.panelFor(this.activeIndex);
        if (panel) {
          var firstLink = panel.querySelector('a, button');
          if (firstLink) firstLink.focus();
        }
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.activeIndex === null) {
        this.open(this.indexFor(e.currentTarget));
      }

      var panel = this.panelFor(this.activeIndex);
      if (panel) {
        var firstLink = panel.querySelector('a, button');
        if (firstLink) firstLink.focus();
      }
    }
  }

  handlePanelEnter() {
    this.cancelClose();
  }

  handlePanelLeave() {
    this.scheduleClose();
  }

  handlePanelKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      var trigger = this.triggerFor(this.activeIndex);
      if (trigger) trigger.focus();
    }

    if (e.key === 'Tab') {
      var panel = e.currentTarget;
      var focusables = Array.from(panel.querySelectorAll('a[href], button:not([disabled])'));
      if (focusables.length === 0) return;

      var last = focusables[focusables.length - 1];
      var first = focusables[0];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        var trigger = this.triggerFor(panel.getAttribute('data-mega-panel'));
        if (trigger) trigger.focus();
        this.close();
      } else if (!e.shiftKey && document.activeElement === last) {
        this.close();
      }
    }
  }

  get isOpen() {
    return this.activeIndex !== null;
  }
}

customElements.define('chakrikka-mega-menu', ChakrikkaMegaMenu);
